import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Camera, Upload, CreditCard, CheckCircle, 
  XCircle, AlertCircle, Loader2, Shield, Scan
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function FaceVerification() {
  const navigate = useNavigate();
  const { token, user, refreshUser } = useAuth();
  
  const idInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [step, setStep] = useState(1); // 1: ID upload, 2: verifying, 3: result
  const [idImage, setIdImage] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  
  // Camera states for ID capture
  const [showIdCameraDialog, setShowIdCameraDialog] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  // Start camera stream for ID capture
  const startIdCamera = useCallback(async () => {
    setCameraError(null);
    setCameraReady(false);
    
    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera for ID
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found. Please connect a camera and try again.');
      } else {
        setCameraError(`Camera error: ${err.message}`);
      }
    }
  }, []);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  }, []);

  // Capture ID frame from video
  const captureIdFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw the video frame (no mirroring for ID)
    context.drawImage(video, 0, 0);
    
    return canvas.toDataURL('image/jpeg', 0.9);
  }, []);

  // Complete ID capture
  const captureIdPhoto = useCallback(() => {
    const frame = captureIdFrame();
    if (frame) {
      fetch(frame)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'id_document.jpg', { type: 'image/jpeg' });
          setIdImage(file);
          setIdPreview(frame);
          setShowIdCameraDialog(false);
          stopCamera();
          toast.success('ID photo captured!');
        });
    }
  }, [captureIdFrame, stopCamera]);

  // Handle camera dialog open
  const openIdCameraDialog = useCallback(() => {
    setShowIdCameraDialog(true);
    setTimeout(() => startIdCamera(), 100);
  }, [startIdCamera]);

  // Handle camera dialog close
  const closeIdCameraDialog = useCallback(() => {
    setShowIdCameraDialog(false);
    stopCamera();
  }, [stopCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const handleIdUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setIdImage(file);
    setIdPreview(URL.createObjectURL(file));
  };

  const uploadAndVerify = async () => {
    if (!idImage) {
      toast.error('Please upload or capture your ID document');
      return;
    }
    
    setUploading(true);
    try {
      // Upload ID document
      const formData = new FormData();
      formData.append('file', idImage);
      
      await axios.post(`${API}/verification/upload-id?document_type=government_id`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('ID document uploaded! Verifying...');
      setStep(2);
      
      // Verify identity (name matching)
      setVerifying(true);
      const response = await axios.post(`${API}/verification/verify-face`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResult(response.data);
      setStep(3);
      
      if (response.data.verified) {
        refreshUser?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
      setResult({
        verified: false,
        message: error.response?.data?.detail || 'Verification failed. Please try again with a clearer ID photo.'
      });
      setStep(3);
    } finally {
      setVerifying(false);
      setUploading(false);
    }
  };

  const resetVerification = () => {
    setStep(1);
    setIdImage(null);
    setIdPreview(null);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-stone-200">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="text-lg font-semibold text-slate-900">Identity Verification</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s 
                  ? 'bg-amber-500 text-slate-900' 
                  : 'bg-stone-200 text-slate-500'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 2 && (
                <div className={`w-16 h-1 mx-1 rounded ${
                  step > s ? 'bg-amber-500' : 'bg-stone-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: ID Document Upload */}
        {step === 1 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Upload Your ID</h2>
              <p className="text-slate-500 text-sm">
                Take a clear photo of your government-issued ID. We'll verify your identity by matching the name on your ID with your account name.
              </p>
            </div>

            {/* Name display */}
            <div className="p-4 bg-slate-50 rounded-xl mb-6">
              <p className="text-sm text-slate-500 mb-1">Your registered name:</p>
              <p className="text-lg font-semibold text-slate-900">{user?.name || 'Loading...'}</p>
              <p className="text-xs text-slate-400 mt-2">
                Make sure the name on your ID matches this name
              </p>
            </div>

            {idPreview ? (
              <div className="relative mb-6">
                <img 
                  src={idPreview} 
                  alt="ID preview" 
                  className="w-full aspect-video object-cover rounded-xl border-2 border-green-500"
                />
                <button
                  onClick={() => { setIdImage(null); setIdPreview(null); }}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <XCircle className="w-5 h-5" />
                </button>
                <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  ID Photo Ready
                </div>
              </div>
            ) : (
              <div 
                onClick={openIdCameraDialog}
                className="aspect-video bg-stone-100 rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:bg-stone-50 transition-colors mb-6"
                data-testid="id-capture-area"
              >
                <Scan className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500 font-medium">Tap to take ID photo</p>
                <p className="text-xs text-slate-400 mt-1">Position your ID within the frame</p>
              </div>
            )}

            <input
              ref={idInputRef}
              type="file"
              accept="image/*"
              onChange={handleIdUpload}
              className="hidden"
            />

            <div className="p-4 bg-amber-50 rounded-xl mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Tips for best results:</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700">
                    <li>Use good lighting - avoid glare</li>
                    <li>Make sure all text is clearly readable</li>
                    <li>Include the full ID in the frame</li>
                    <li>Keep the camera steady</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={uploadAndVerify}
                disabled={!idImage || uploading}
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900"
                data-testid="verify-btn"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Shield className="w-5 h-5 mr-2" />}
                Verify My Identity
              </Button>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={openIdCameraDialog}
                  className="flex-1 h-12"
                  data-testid="open-id-camera-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => idInputRef.current?.click()}
                  className="flex-1 h-12"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Verifying */}
        {step === 2 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Verifying Your Identity</h2>
            <p className="text-slate-500">
              Reading your ID and matching your name...
            </p>
            <div className="mt-4 text-sm text-slate-400">
              This may take a few seconds
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="text-center mb-6">
              {result.verified ? (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-green-800 mb-2">Verification Successful!</h2>
                  <p className="text-green-600">{result.message}</p>
                  <div className="mt-4 p-4 bg-green-50 rounded-xl space-y-2">
                    {result.name_match !== undefined && result.name_match > 0 && (
                      <p className="text-sm text-green-700">
                        Name Match Score: <span className="font-bold">{result.name_match}%</span>
                      </p>
                    )}
                    <p className="text-xs text-green-600">
                      Your identity has been verified successfully
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-10 h-10 text-amber-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-amber-800 mb-2">Verification Pending</h2>
                  <p className="text-amber-600 mb-4">{result.message}</p>
                  <div className="p-4 bg-amber-50 rounded-xl mb-4 space-y-2">
                    {result.name_match !== undefined && (
                      <p className="text-sm text-amber-700">
                        Name Match Score: <span className="font-bold">{result.name_match}%</span>
                      </p>
                    )}
                    <p className="text-xs text-amber-600">
                      Your ID has been submitted for manual review
                    </p>
                  </div>
                  {/* Show tips */}
                  {result.tips && result.tips.length > 0 && (
                    <div className="p-4 bg-blue-50 rounded-xl text-left">
                      <p className="text-sm font-medium text-blue-800 mb-2">Tips to improve verification:</p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        {result.tips.map((tip, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-blue-500">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="space-y-3">
              {result.verified ? (
                <Button 
                  onClick={() => navigate(user?.role === 'provider' ? '/provider/profile' : '/profile')}
                  className="w-full h-12 bg-green-600 hover:bg-green-700"
                  data-testid="back-to-profile-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Back to Profile
                </Button>
              ) : (
                <>
                  <Button 
                    onClick={resetVerification}
                    className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900"
                    data-testid="try-again-btn"
                  >
                    Try Again
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate(user?.role === 'provider' ? '/provider/profile' : '/profile')}
                    className="w-full h-12"
                  >
                    Back to Profile
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Info Section */}
        {step === 1 && (
          <div className="mt-8 p-4 bg-blue-50 rounded-xl">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How It Works</p>
                <p className="text-blue-700">
                  We use OCR technology to read the name from your ID and match it with your registered account name. 
                  This helps ensure everyone on the platform is who they claim to be.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Camera Dialog for ID Capture with Bounding Box */}
      <Dialog open={showIdCameraDialog} onOpenChange={(open) => !open && closeIdCameraDialog()}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-500" />
              Capture ID Document
            </DialogTitle>
            <DialogDescription>
              Position your ID card within the frame below
            </DialogDescription>
          </DialogHeader>
          
          <div className="px-4 pb-4">
            {/* Camera Error */}
            {cameraError && (
              <div className="p-4 bg-red-50 rounded-xl mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Camera Error</p>
                    <p className="text-xs text-red-600">{cameraError}</p>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={startIdCamera}
                      className="mt-2"
                    >
                      Retry
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Video Preview with ID Bounding Box */}
            <div className="relative aspect-video bg-black rounded-xl overflow-hidden mb-4">
              <video 
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} className="hidden" />
              
              {/* ID Card Bounding Box Overlay */}
              {cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {/* Semi-transparent overlay outside the ID frame */}
                  <div className="absolute inset-0 bg-black/40" />
                  
                  {/* ID Card Frame - clear area */}
                  <div className="relative w-[85%] h-[75%] max-w-sm">
                    {/* Clear center for ID */}
                    <div className="absolute inset-0 bg-transparent border-4 border-amber-400 rounded-lg shadow-lg" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)' }} />
                    
                    {/* Corner markers */}
                    <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-amber-400 rounded-tl-lg" />
                    <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-amber-400 rounded-tr-lg" />
                    <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-amber-400 rounded-bl-lg" />
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-amber-400 rounded-br-lg" />
                    
                    {/* Instructions */}
                    <div className="absolute -top-10 left-0 right-0 text-center">
                      <span className="text-white text-sm font-medium bg-black/60 px-3 py-1 rounded-full">
                        Align your ID within this frame
                      </span>
                    </div>
                    
                    {/* ID icon indicator */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-30">
                      <CreditCard className="w-24 h-24 text-white" />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Loading State */}
              {!cameraReady && !cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                  <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-amber-500 mx-auto mb-2" />
                    <p className="text-white text-sm">Starting camera...</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Tips */}
            <div className="p-3 bg-blue-50 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <AlertCircle className="w-4 h-4 text-blue-500 shrink-0" />
                <span>Hold steady and make sure all text is readable</span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={closeIdCameraDialog}
                className="flex-1"
              >
                Cancel
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  closeIdCameraDialog();
                  idInputRef.current?.click();
                }}
                className="flex-1"
              >
                <Upload className="w-4 h-4 mr-2" />
                Gallery
              </Button>
              
              {cameraReady && (
                <Button
                  onClick={captureIdPhoto}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900"
                  data-testid="capture-id-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Capture
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
